import React, { Fragment, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { Paper } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { UserMode } from '../redux/types/userTypes';

// ✅ CONSTANTES CENTRALIZADAS
const NAVIGATION_CONFIG = {
  Z_INDEX: 10000,
  BORDER_RADIUS: '20px 20px 0 0',
  PADDING: '25px',
  ICON_SIZE: 60,
  ICON_BORDER: '4px',
  ICON_PADDING: '10px',
  ICON_MARGIN_BOTTOM: '12px',
  TRANSITION_DURATION: '0.25s',
} as const;

// ✅ ESTILOS EXTRACTADOS Y CENTRALIZADOS
const useNavBarStyles = () => {
  return useMemo(
    () => ({
      navbar: {
        backgroundColor: 'primary.main',
        width: '100vw',
        paddingBottom: NAVIGATION_CONFIG.PADDING,
        paddingTop: NAVIGATION_CONFIG.PADDING,
        borderRadius: NAVIGATION_CONFIG.BORDER_RADIUS,
        '& .MuiBottomNavigationAction-root, .Mui-selected, svg': {
          color: 'white',
          transition: `all ${NAVIGATION_CONFIG.TRANSITION_DURATION} ease-in-out`,
          marginBottom: NAVIGATION_CONFIG.ICON_MARGIN_BOTTOM,
          borderRadius: '100%',
        },
        '& .Mui-selected': {
          color: 'primary.main',
        },
        '& .Mui-selected > svg': {
          fill: 'primary.main',
          color: 'primary.main',
          backgroundImage: 'linear-gradient(#7be7c6, #3EA8E3)',
          border: `solid ${NAVIGATION_CONFIG.ICON_BORDER} #1b4ed8`,
          padding: NAVIGATION_CONFIG.ICON_PADDING,
          width: `${NAVIGATION_CONFIG.ICON_SIZE}px`,
          height: `${NAVIGATION_CONFIG.ICON_SIZE}px`,
        },
      },
      paper: {
        background: 'transparent',
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: NAVIGATION_CONFIG.Z_INDEX,
      },
    }),
    []
  );
};

// ✅ INTERFACE PARA NAVIGATION ITEMS
interface NavigationItem {
  value: string;
  icon: React.ReactElement;
  label?: string;
}

export default function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useSelector((state: RootState) => state.user);
  const styles = useNavBarStyles();

  // ✅ NAVIGATION ITEMS CENTRALIZADOS
  const navigationItems: NavigationItem[] = useMemo(
    () => [
      { value: '/home', icon: <HomeIcon />, label: 'Inicio' },
      { value: '/body', icon: <AccessibilityIcon />, label: 'Cuerpo' },
      { value: '/profile', icon: <PersonIcon />, label: 'Perfil' },
      {
        value: mode !== UserMode.VIEWER ? '/out' : '/waiting',
        icon: <ExitToAppIcon />,
        label: mode !== UserMode.VIEWER ? 'Salir' : 'Esperando',
      },
    ],
    [mode]
  );

  // ✅ RUTAS DONDE MOSTRAR LA NAVBAR
  const shouldShowNavBar = useMemo(() => {
    const navRoutes = ['/home', '/body', '/profile', '/out', '/waiting'];
    return navRoutes.includes(location.pathname);
  }, [location.pathname]);

  const handleNavigationChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    navigate(newValue);
  };

  // ✅ NO RENDERIZAR SI NO DEBE MOSTRARSE
  if (!shouldShowNavBar) {
    return null;
  }

  return (
    <Fragment>
      <Paper sx={styles.paper} elevation={2}>
        <BottomNavigation
          sx={styles.navbar}
          value={location.pathname}
          onChange={handleNavigationChange}
          showLabels
        >
          {navigationItems.map(item => (
            <BottomNavigationAction
              key={item.value}
              value={item.value}
              icon={item.icon}
              label={item.label}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Fragment>
  );
}
